import React, { useEffect } from "react";

import { css, cx } from "@emotion/css";
import { useWatch, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFormError } from "@/hooks/useFormError.ts";
import { useRichTextState } from "@/hooks/useRichTextState.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { FormInputError } from "@/uiKit/atoms/forms/FormInputError.tsx";
import { FormFileInput } from "@/uiKit/molecules/form/FormFileInput.tsx";
import { RichText } from "@/uiKit/molecules/RichText/RichText.tsx";

type PatientInfo = {
  givenName: string;
  familyName: string;
};

export type ObjectivesFormFieldProps = {
  patientInfo: PatientInfo;
  nameForText?: string;
  nameForFile?: string;
  className?: string;
};

export const ObjectivesFormField: React.FC<ObjectivesFormFieldProps> = ({
  patientInfo,
  nameForText = "description",
  nameForFile = "descriptionAttachment",
  className,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const formContext = useFormContext();
  const objectivesText = useWatch({
    name: nameForText,
    control: formContext.control,
  }) as string | undefined;
  const objectivesFile = useWatch({
    name: nameForFile,
    control: formContext.control,
  });

  const richTextState = useRichTextState(objectivesText, {
    initialState: "edit",
    onChange: text => {
      formContext.setValue(nameForText, text, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
  });

  useEffect(() => {
    if (formContext.formState.dirtyFields?.[nameForFile]) {
      formContext.setValue(nameForText, objectivesText, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [objectivesFile]);

  const objectivesError = useFormError(nameForText);

  return (
    <div className={cx(styles.root, className)}>
      <div className={cx({ [styles.content]: true, [styles.errorContent]: objectivesError.hasError })}>
        <RichText
          createNewTextTitle={t("diabetesForms.writeGoals")}
          editTextTitle={t("diabetesForms.editGoals")}
          richTextState={richTextState}
          placeholder={t("diabetesForms.writeGoals")}
          hideActions
          hideHeader
          classes={{ editorContent: styles.richText }}
        />
        <FormFileInput
          id="objectives-file-form-field"
          name={nameForFile}
          label={t("diabetesForms.goalsShort")}
          uploadLabel={t("inputs.browse")}
          fileType="description"
          defaultValue={formContext.formState.defaultValues?.[nameForFile]}
          patientInfo={patientInfo}
          classes={{ input: styles.fileManager }}
          fullWidth
        />
      </div>
      <FormInputError
        reserveSpace
        errorMessage={objectivesError.errorMessage ? t(objectivesError.errorMessage) : undefined}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({}),
  content: css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    borderColor: theme.palette.grey[300],
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: theme.spacing(4),
  }),
  errorContent: css({
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  }),
  fileManager: css({
    margin: theme.spacing(0, 4, 4, 4),
    //borderRadius: theme.spacing(4),
    //backgroundImage: "none",
  }),
  richText: css({
    border: "none",
    borderWidth: 0,
    padding: theme.spacing(4, 8),
  }),
});
